import { Tooltip } from 'react-tippy'
import styled from 'styled-components'

import { fonts, palette } from '../../../styles/variables'
import { Heading2A, Text } from '../../../components/topography'
import { translate } from '../../../utils/translation'
import {
  DataColumn,
  DataRow,
  HeadingColumn,
  TableInnerWrapper,
  TableOuterWrapper,
} from '../../../components/tables/styled'
import { formatNumber } from '../../../utils/numbers'
import { LoadingWheel } from '../../../components/loading/Loading'

import { InsightPanel, InsightDataLoader, SiteDataContainer } from './styled'

const SHeadingRow = styled.div(() => ({
  width: '100%',
  height: 'fit-content',
  display: 'flex',
  borderRadius: '4px',
}))

const SHeadingColumn = styled(HeadingColumn)({
  backgroundColor: palette.white,
  padding: '16px',
  fontFamily: fonts.primaryBold,
  fontSize: '12px',
  lineHeight: '16px',
  color: palette.darkGray,
  flex: '1 0 100px',
  borderTop: `1px solid ${palette.lightGray}`,
  borderRight: `1px solid ${palette.white}`,
  ':first-child': {
    flex: '1.5 0 90px',
    borderRight: `1px solid ${palette.lightGray}`,
    paddingLeft: '24px',
  },
  ':nth-child(4)': {
    paddingRight: '24px',
  },
})

const SDataRow = styled(DataRow)(() => ({
  width: '100%',
  height: 'fit-content',
  display: 'flex',
  borderRadius: '4px',
}))

const SDataColumn = styled(DataColumn)({
  backgroundColor: palette.lightGray20,
  padding: '16px',
  fontFamily: fonts.primary,
  cursor: 'default',
  color: palette.darkGray,
  borderRight: `1px solid ${palette.white}`,
  fontSize: '16px',
  lineHeight: '24px',
  justifyContent: 'flex-start',
  flex: '1 0 100px',
  ':first-child': {
    flex: '1.5 0 90px',
    borderRight: `1px solid ${palette.lightGray}`,
    fontSize: '14px',
    lineHeight: '24px',
    paddingLeft: '24px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  ':nth-child(4)': {
    paddingRight: '24px',
  },
})

const renderMetric = (isLoading, value) => {
  if (isLoading) return <LoadingWheel width={24} height={24} />
  if (!isNaN(value)) return formatNumber(value)
  return (
    <Text fontSize="16px" lineHeight="24px" color={palette.gray}>
      {translate('not_available')}
    </Text>
  )
}

const SiteInsightPreview = ({ domain, seoPerformance, siteData, step }) => {
  const { domainOverview, competitors } = seoPerformance
  const hasSeoPerformanceData =
    domainOverview.hasData ||
    (competitors.length > 0 ? competitors.some((c) => c.hasData) : undefined)
  return (
    <SiteDataContainer>
      <Text
        fontFamily={fonts.primaryBook}
        fontSize="20px"
        lineHeight="32px"
      >{`${translate('website_overview')} - ${domain}`}</Text>
      <InsightPanel
        margin="24px 0 0 "
        padding={hasSeoPerformanceData ? '0px' : '24px'}
      >
        <Heading2A
          style={{
            padding: hasSeoPerformanceData ? '24px 24px 0 24px' : '0px',
          }}
        >
          {translate('seo_performance')}
        </Heading2A>
        <InsightDataLoader
          testId="insight-loader-seo-performance"
          isLoading={domainOverview.isLoading}
          hasData={hasSeoPerformanceData}
          warningText={translate('empty_state_seo_performance')}
        >
          <Text
            data-testid="seo-performance-title"
            fontSize="12px"
            lineHeight="18px"
            color={palette.darkGray70}
            boldColor={palette.darkGray70}
            margin="8px 0 0 0"
            padding="0 24px"
            dangerouslySetInnerHTML={{
              __html: translate('performance_last_month'),
            }}
          />
          <TableOuterWrapper style={{ marginTop: '16px' }}>
            <TableInnerWrapper styles={{ overflowX: 'scroll' }}>
              <SHeadingRow>
                <SHeadingColumn>{translate('website')}</SHeadingColumn>
                <SHeadingColumn>
                  {translate('domain_authority_lb')}
                </SHeadingColumn>
                <SHeadingColumn>
                  {translate('dashboard_organic_traffic')}
                </SHeadingColumn>
                <SHeadingColumn>{translate('organic_keywords')}</SHeadingColumn>
              </SHeadingRow>
              <SDataRow>
                <SDataColumn>
                  <Tooltip
                    title={domainOverview.domain}
                    position="bottom"
                    arrow
                    style={{
                      width: '100%',
                    }}
                    popperOptions={{
                      modifiers: {
                        preventOverflow: {
                          enabled: true,
                        },
                        hide: {
                          enabled: true,
                        },
                        addZIndex: {
                          enabled: true,
                          order: 810,
                          fn: (data) => ({
                            ...data,
                            styles: {
                              ...data.styles,
                              zIndex: 100001,
                            },
                          }),
                        },
                      },
                    }}
                  >
                    <Text
                      data-testid="websiteValue"
                      style={{
                        textOverflow: 'ellipsis',
                        overflow: 'hidden',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {domainOverview.domain}
                    </Text>
                  </Tooltip>
                </SDataColumn>
                <SDataColumn data-testid="domainAuthorityValue">
                  {renderMetric(
                    domainOverview.isLoading,
                    domainOverview.domainAuthority,
                  )}
                </SDataColumn>
                <SDataColumn data-testid="trafficValue">
                  {renderMetric(
                    domainOverview.isLoading,
                    domainOverview.traffic,
                  )}
                </SDataColumn>
                <SDataColumn data-testid="organicValue">
                  {renderMetric(
                    domainOverview.isLoading,
                    domainOverview.organic,
                  )}
                </SDataColumn>
              </SDataRow>
              {seoPerformance.competitors.map((competitor) => (
                <SDataRow key={competitor.domain}>
                  <SDataColumn>
                    <Tooltip
                      title={competitor.domain}
                      position="bottom"
                      arrow
                      style={{
                        width: '100%',
                      }}
                      popperOptions={{
                        preventOverflow: {
                          enabled: true,
                        },
                        hide: {
                          enabled: true,
                        },
                        modifiers: {
                          addZIndex: {
                            enabled: true,
                            order: 810,
                            fn: (data) => ({
                              ...data,
                              styles: {
                                ...data.styles,
                                zIndex: 100001,
                              },
                            }),
                          },
                        },
                      }}
                    >
                      <Text
                        style={{
                          textOverflow: 'ellipsis',
                          overflow: 'hidden',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {competitor.domain}
                      </Text>
                    </Tooltip>
                  </SDataColumn>
                  <SDataColumn data-testid="competitorsDomainAuthority">
                    {renderMetric(
                      competitor.isLoading,
                      competitor.domainAuthority,
                    )}
                  </SDataColumn>
                  <SDataColumn data-testid="competitorsOrganicTraffic">
                    {renderMetric(competitor.isLoading, competitor.traffic)}
                  </SDataColumn>
                  <SDataColumn data-testid="competitorsOrganicKeywords">
                    {renderMetric(competitor.isLoading, competitor.organic)}
                  </SDataColumn>
                </SDataRow>
              ))}
            </TableInnerWrapper>
          </TableOuterWrapper>
        </InsightDataLoader>
      </InsightPanel>
      <InsightPanel margin="24px 0 0 ">
        <Heading2A>{translate('meta_information')}</Heading2A>
        <InsightDataLoader
          testId="insight-loader-site-meta"
          isLoading={siteData.isLoading}
          hasData={siteData.hasData}
          step={step}
          warningText={translate('empty_state_meta_information')}
          subWarningTexts={[
            translate('enter_your_website'),
            translate('enter_language_location'),
          ]}
        >
          <Text
            data-testid="site-meta-title"
            lineHeight="24px"
            color={palette.blue}
            fontFamily={fonts.primaryBook}
            margin="8px 0 0 0"
          >
            {siteData.title}
          </Text>
          <Text
            fontSize="12px"
            lineHeight="18px"
            color={palette.darkGray70}
            margin="8px 0 0 0"
          >
            {siteData.description}
          </Text>
        </InsightDataLoader>
      </InsightPanel>
      <InsightPanel margin="24px 0 0 ">
        <Heading2A>{translate('site_preview')}</Heading2A>
        <InsightDataLoader
          testId="insight-loader-site-preview"
          isLoading={siteData.isLoading}
          hasData={siteData.hasData}
          step={step}
          warningText={translate('empty_state_site_preview')}
          subWarningTexts={[
            translate('enter_your_website'),
            translate('enter_language_location'),
          ]}
        >
          <Text
            data-testid="site-preview-domain"
            fontSize="12px"
            lineHeight="18px"
            textAlign="center"
            margin="16px 0"
            style={{
              border: `1px solid ${palette.lightGray}`,
              borderRadius: '100px',
              padding: '8px',
            }}
          >
            {domain}
          </Text>
          {siteData.screenshot_url && (
            <img
              src={siteData.screenshot_url}
              alt="site screenshot"
              style={{
                width: '100%',
                borderBottomRightRadius: '6px',
                borderBottomLeftRadius: '6px',
              }}
            />
          )}
        </InsightDataLoader>
      </InsightPanel>
    </SiteDataContainer>
  )
}

export default SiteInsightPreview
